import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthStore } from '@store'
import toast from 'react-hot-toast'
import { CATEGORIES } from '@constants/categories'
import { postService } from '@services/api/posts'
import apiClient from '@services/api/client'
import { storageURL, mediaUrl } from '@config'

function EditBannerPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    link: '',
    bannerImage: null,
    bannerFile: null,
    currentImageUrl: ''
  })

  const categories = CATEGORIES

  useEffect(() => {
    fetchBanner()
  }, [id])

  const fetchBanner = async () => {
    try {
      setLoading(true)
      const res = await postService.getPostById(id)
      const p = res.data || res
      setFormData({
        title: p.libele || '',
        category: '',
        description: p.description || '',
        link: p.siteWeb || '',
        bannerImage: null,
        bannerFile: null,
        currentImageUrl: p.fichiers?.[0]?.chemin ? mediaUrl(p.fichiers[0].chemin) : ''
      })
    } catch (error) {
      console.error('Erreur lors du chargement de la bannière:', error)
      toast.error('Erreur lors du chargement')
      navigate('/dashboard')
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('image/')) {
      setFormData({ 
        ...formData, 
        bannerImage: URL.createObjectURL(file),
        bannerFile: file 
      })
    } else {
      toast.error('Veuillez sélectionner une image valide')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.title || !formData.link || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    setIsSubmitting(true)

    try {
      let data

      if (formData.bannerFile) {
        // Avec fichier : POST + _method=PUT (PHP ne parse pas multipart/PUT)
        const formDataToSend = new FormData()
        formDataToSend.append('_method', 'PUT')
        formDataToSend.append('libele', formData.title)
        formDataToSend.append('description', formData.description)
        formDataToSend.append('siteWeb', formData.link)
        formDataToSend.append('files[]', formData.bannerFile)
        data = await apiClient.post(`/posts/${id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } else {
        // Sans fichier : PUT JSON standard
        data = await apiClient.put(`/posts/${id}`, {
          libele: formData.title,
          description: formData.description,
          siteWeb: formData.link,
        })
      }

      if (data.success) {
        toast.success('Bannière modifiée avec succès !')
        navigate('/dashboard')
      } else {
        throw new Error(data.message || 'Erreur inconnue')
      }

    } catch (error) {
      console.error('Erreur lors de la modification:', error)
      const msg = error.response?.data?.message
        || error.response?.data?.errors
        || error.message
        || 'Erreur lors de la modification de la bannière'
      toast.error(typeof msg === 'object' ? JSON.stringify(msg) : msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fefcf8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#fefcf8]">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 lg:p-12">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-[#001F3F]">Modifier la bannière</h1>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Colonne gauche */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Titre<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Titre de votre bannière"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Catégorie<span className="text-red-600">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent appearance-none bg-white"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.filter(cat => cat !== 'Toutes les catégories').map((cat, i) => (
                      <option key={i} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Lien URL<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="url"
                    name="link"
                    value={formData.link}
                    onChange={handleChange}
                    placeholder="https://exemple.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Description<span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="6"
                    placeholder="Description de votre bannière..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent resize-none"
                    required
                  />
                </div>
              </div>

              {/* Colonne droite - Image */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Image de la bannière
                  </label>
                  
                  {/* Aperçu de l'image actuelle */}
                  {(formData.bannerImage || formData.currentImageUrl) && (
                    <div className="mb-4">
                      <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                        <img
                          src={formData.bannerImage || formData.currentImageUrl}
                          alt="Aperçu"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.onerror = null; e.target.style.display = 'none' }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {formData.bannerFile ? 'Nouvelle image sélectionnée' : 'Image actuelle'}
                      </p>
                    </div>
                  )}

                  {/* Zone d'upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#D4AF37] transition-colors cursor-pointer">
                    <input
                      type="file"
                      id="bannerImage"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <label htmlFor="bannerImage" className="cursor-pointer">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-600 mb-1">Changer l'image de la bannière</p>
                      <p className="text-sm text-gray-500">Format recommandé: 1200x400px</p>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-4 justify-end pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Modification...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditBannerPage
